import { computed, inject, Injector, type Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { type ActivatedRouteSnapshot, type CanActivateFn, Router, type UrlTree } from '@angular/router';
import { filter, map, type Observable, take } from 'rxjs';

import { StatisticsType } from '@malou-io/package-utils';

import { AbilitiesContext } from '#core/context/abilities.context';
import { StatisticsPermissionsService } from '#core/services/statistics-permissions.service';
import { SessionStore } from '#core/store/session.store';
import { RestaurantsStore } from '#modules/restaurants/stores/restaurants.store';

/** Tab segment per area, mirroring the statistics routing. */
const STATISTICS_TAB_SEGMENT: Record<StatisticsType, string> = {
    [StatisticsType.SEO]: 'seo',
    [StatisticsType.E_REPUTATION]: 'e-reputation',
    [StatisticsType.SOCIAL]: 'social-networks',
    [StatisticsType.BOOSTERS]: 'boosters',
    [StatisticsType.ROI]: 'roi',
};

/** The restaurant id can sit on a parent route, since the tabs are children of the statistics layout. */
const restaurantIdOf = (route: ActivatedRouteSnapshot): string | undefined => {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
        const restaurantId = current.params.restaurant_id ?? current.params.restaurantId;
        if (restaurantId) {
            return restaurantId;
        }
        current = current.parent;
    }
    return undefined;
};

/**
 * Whether the session knows who the user is, or gave up trying.
 * `StatisticsPermissionsService` lets an admin read everything, and the session user loads in parallel with the abilities, so a verdict taken on the abilities alone could deny an admin whose session is still loading.
 * The session store starts that load as soon as it is created, so waiting on it never waits on a load that has not started.
 */
const isSessionSettled = (sessionStore: Pick<InstanceType<typeof SessionStore>, 'isLoaded' | 'isError'>): boolean =>
    sessionStore.isLoaded() || sessionStore.isError();

/**
 * Decides once the abilities are loaded, never before: both start empty, which denies everything, so an earlier read would mistake "not loaded yet" for "not allowed".
 * A user who may not read the requested area is sent to their first readable one, and out of the statistics section when none is readable — never to an error page.
 */
const decideOnce = (isReady: Signal<boolean>, decide: () => boolean | UrlTree): Observable<boolean | UrlTree> => {
    const injector = inject(Injector);

    return toObservable(isReady, { injector }).pipe(filter(Boolean), take(1), map(decide));
};

export const restaurantStatisticsReadGuard: CanActivateFn = (route) => {
    const router = inject(Router);
    const statisticsPermissionsService = inject(StatisticsPermissionsService);

    const abilitiesContext = inject(AbilitiesContext);
    const sessionStore = inject(SessionStore);
    const isReady = computed(() => abilitiesContext.areAbilitiesLoaded() && isSessionSettled(sessionStore));

    return decideOnce(isReady, () => {
        const requestedType = route.data.statisticsType as StatisticsType | undefined;
        const restaurantId = restaurantIdOf(route);
        if (!requestedType || !restaurantId) {
            return true;
        }
        if (statisticsPermissionsService.canRead(restaurantId, requestedType)) {
            return true;
        }
        const firstReadableType = statisticsPermissionsService.getFirstReadableType(restaurantId);
        return router.createUrlTree(
            firstReadableType
                ? ['/restaurants', restaurantId, 'statistics', STATISTICS_TAB_SEGMENT[firstReadableType]]
                : ['/restaurants', restaurantId]
        );
    });
};

export const aggregatedStatisticsReadGuard: CanActivateFn = (route) => {
    const router = inject(Router);
    const statisticsPermissionsService = inject(StatisticsPermissionsService);
    const restaurantsStore = inject(RestaurantsStore);

    const abilitiesContext = inject(AbilitiesContext);
    const sessionStore = inject(SessionStore);
    // The organization is auto-selected in an effect once the restaurants land, so the per-organization list is briefly empty even after the store reports loaded.
    // Falling back to every restaurant of the user keeps the check meaningful instead of waving the route through on a list that is merely not ready.
    const isReady = computed(() => abilitiesContext.areAbilitiesLoaded() && restaurantsStore.isLoaded() && isSessionSettled(sessionStore));

    return decideOnce(isReady, () => {
        const requestedType = route.data.statisticsType as StatisticsType | undefined;
        const restaurantsOfOrganization = restaurantsStore.restaurantsOfSelectedOrganization();
        const restaurants = restaurantsOfOrganization.length ? restaurantsOfOrganization : restaurantsStore.restaurants();
        const restaurantIds = restaurants.map((restaurant) => restaurant.id);
        if (!requestedType) {
            return true;
        }
        if (statisticsPermissionsService.canReadForAnyRestaurant(restaurantIds, requestedType)) {
            return true;
        }
        const firstReadableType = statisticsPermissionsService.getFirstReadableTypeForAnyRestaurant(restaurantIds);
        return router.createUrlTree(firstReadableType ? ['/groups', 'statistics', STATISTICS_TAB_SEGMENT[firstReadableType]] : ['/groups']);
    });
};
